import type { ConfigDto } from '@zakat/shared';
import { http } from './api';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

export const pushSupported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Demande la permission (geste utilisateur requis) et enregistre l'abonnement côté API. */
export async function enablePush(topics?: string[]): Promise<boolean> {
  if (!pushSupported()) return false;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  const config = await http.get<ConfigDto>('/config', { anonymous: true });
  if (!config.vapidPublicKey) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(config.vapidPublicKey) }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await http.post('/notifications/subscriptions', { endpoint: json.endpoint, keys: json.keys, topics });
  return true;
}

/** Ré-associe l'abonnement existant au compte après connexion. */
export async function syncPushSubscription(): Promise<void> {
  const sub = await currentPushSubscription();
  if (!sub || Notification.permission !== 'granted') return;
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await http.post('/notifications/subscriptions', { endpoint: json.endpoint, keys: json.keys }).catch(() => undefined);
}

export async function disablePush(): Promise<void> {
  const sub = await currentPushSubscription();
  if (!sub) return;
  await http.delete('/notifications/subscriptions', { body: { endpoint: sub.endpoint } }).catch(() => undefined);
  await sub.unsubscribe();
}
